import { useRef, useCallback } from 'react';
import type { RefObject } from 'react';
import type { TableBlockHandle } from '../../core/types';
import { BlockEventType } from '../../core/constants';
import { useEventScope } from '../useEventScope';
import { useBlockHandle } from '../useBlockHandle';

interface DynamicTableV2HandleLike {
  refreshData: () => void;
  resetSelection: () => void;
  getSelectedKeys: () => string[];
}

interface UseTableEventAdapterOptions {
  onExternalFilter?: (filters: Record<string, unknown>) => void;
  onExternalParams?: (params: Record<string, unknown>) => void;
  getTotalCount?: () => number;
}

export function useTableEventAdapter(
  semanticId: string | undefined,
  tableRef: RefObject<DynamicTableV2HandleLike | null>,
  options?: UseTableEventAdapterOptions,
) {
  const scope = useEventScope();
  const totalCountRef = useRef(0);

  const handleFactory = useCallback((): TableBlockHandle => {
    return {
      semanticId: semanticId!,
      blockType: 'table',
      capabilities: new Set([
        'refreshData',
        'resetSelection',
        'setFilter',
        'setParams',
      ]),
      refreshData: () => tableRef.current?.refreshData(),
      resetSelection: () => tableRef.current?.resetSelection(),
      getSelectedKeys: () => tableRef.current?.getSelectedKeys() ?? [],
      setFilter: (filters) => options?.onExternalFilter?.(filters),
      setParams: (params) => options?.onExternalParams?.(params),
      getTotalCount: () => options?.getTotalCount?.() ?? totalCountRef.current,
      dispose: () => {},
    };
  }, [semanticId, tableRef, options]);

  useBlockHandle(semanticId, handleFactory, [tableRef, options]);

  const publishActionExecuted = useCallback(
    (actionId: string, result?: unknown, selectedKeys?: string[]) => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.ACTION_EXECUTED, semanticId, {
        actionId,
        result,
        selectedKeys,
      });
    },
    [scope, semanticId],
  );

  const publishDataLoaded = useCallback(
    (total: number, page: number, pageSize: number) => {
      if (!scope || !semanticId) return;
      totalCountRef.current = total;
      scope.bus.publish(BlockEventType.DATA_LOADED, semanticId, {
        total,
        page,
        pageSize,
      });
    },
    [scope, semanticId],
  );

  const publishSelectionChanged = useCallback(
    (selectedKeys: string[]) => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.SELECTION_CHANGED, semanticId, {
        selectedKeys,
        count: selectedKeys.length,
      });
    },
    [scope, semanticId],
  );

  const publishRowClicked = useCallback(
    (row: Record<string, unknown>, pk: string) => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.ROW_CLICKED, semanticId, { row, pk });
    },
    [scope, semanticId],
  );

  const publishEditStateChanged = useCallback(
    (isEditing: boolean, editingRowKeys: string[]) => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.EDIT_STATE_CHANGED, semanticId, {
        isEditing,
        editingRowKeys,
      });
    },
    [scope, semanticId],
  );

  const publishRowSaved = useCallback(
    (row: Record<string, unknown>, rowKey: string, mode: 'create' | 'update') => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.ROW_SAVED, semanticId, {
        row,
        rowKey,
        mode,
      });
    },
    [scope, semanticId],
  );

  return {
    publishActionExecuted,
    publishDataLoaded,
    publishSelectionChanged,
    publishRowClicked,
    publishEditStateChanged,
    publishRowSaved,
    scope,
  };
}
